/* ==========================================================================
   Lumen — product.js
   Drives <product-info> on the product page: variant resolution, price /
   availability / inventory / SKU / media updates, sticky add-to-cart bar,
   share, external-video facades, lightbox sync, complementary products,
   and the recently-viewed localStorage writer.
   Progressive enhancement only — the plain form + <noscript> variant select
   keep working when this file never loads.
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var RV_KEY = 'lumen:recently-viewed';
  var RV_MAX = 12;

  /* ----------------------------------------------------------------------
     Money formatting (theme.settings.moneyFormat, e.g. "${{amount}}")
  ---------------------------------------------------------------------- */
  var PLACEHOLDER_RE = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(number) || number == null) return '0';
    var fixed = (number / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || (window.theme && theme.settings && theme.settings.moneyFormat) || '${{amount}}';
    var match = fmt.match(PLACEHOLDER_RE);
    var type = match ? match[1] : 'amount';
    var value;
    switch (type) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(PLACEHOLDER_RE, value);
  }

  /* ----------------------------------------------------------------------
     <product-info>
  ---------------------------------------------------------------------- */
  if (!customElements.get('product-info')) {
    customElements.define('product-info', class extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        var jsonEl = this.querySelector('[data-product-json]');
        if (!jsonEl) return;
        try {
          this.product = JSON.parse(jsonEl.textContent);
        } catch (e) {
          return;
        }
        this._initialized = true;

        this.variantMeta = {};
        var metaEl = this.querySelector('[data-variant-meta]');
        if (metaEl) {
          try { this.variantMeta = JSON.parse(metaEl.textContent) || {}; } catch (e) { /* noop */ }
        }

        this.sectionId = this.getAttribute('data-section-id');
        this.form = document.getElementById(this.getAttribute('data-form-id') || '');
        this.picker = this.querySelector('[data-variant-picker]');
        this.gallery = this.querySelector('[data-media-gallery]');
        this.stickyBar = this.querySelector('[data-sticky-atc]');
        this._stickyShown = false;
        this._pendingLightbox = null;

        this.currentVariant = this._initialVariant();

        var self = this;
        this.addEventListener('change', function (e) { self._onChange(e); });
        this.addEventListener('click', function (e) { self._onClick(e); });

        this._initGallery();
        this._initVariantPreview();
        this._initSizeRec();
        this._initStickyBar();
        this._initLightbox();
        this._initComplementary();
        this._initNotify();
        this._writeRecentlyViewed();
      }

      disconnectedCallback() {
        if (this._io) this._io.disconnect();
        if (this._galleryIo) this._galleryIo.disconnect();
      }

      /* -- variant state -------------------------------------------------- */

      _initialVariant() {
        var id = null;
        var hidden = this.form && this.form.querySelector('[data-main-variant-id]');
        if (hidden) id = parseInt(hidden.value, 10);
        var variants = this.product.variants || [];
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].id === id) return variants[i];
        }
        return variants[0] || null;
      }

      _selectedOptions() {
        var options = this.currentVariant ? this.currentVariant.options.slice() : [];
        if (!this.picker) return options;
        this.picker.querySelectorAll('[data-option-position]').forEach(function (control) {
          var pos = parseInt(control.getAttribute('data-option-position'), 10);
          if (!pos) return;
          var value = null;
          if (control.tagName === 'SELECT') {
            value = control.value;
          } else {
            var checked = control.querySelector('input[type="radio"]:checked');
            if (checked) value = checked.value;
          }
          if (value !== null) options[pos - 1] = value;
        });
        return options;
      }

      _findVariant(options) {
        var variants = this.product.variants || [];
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var ok = true;
          for (var j = 0; j < options.length; j++) {
            if (v.options[j] !== options[j]) { ok = false; break; }
          }
          if (ok) return v;
        }
        return null;
      }

      _optionHasAvailableVariant(position, value, selected) {
        var variants = this.product.variants || [];
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var ok = true;
          for (var j = 0; j < selected.length; j++) {
            var want = j === position - 1 ? value : selected[j];
            if (v.options[j] !== want) { ok = false; break; }
          }
          if (ok && v.available) return true;
        }
        return false;
      }

      /* -- event delegation ------------------------------------------------ */

      _onChange(e) {
        if (this.picker && this.picker.contains(e.target) && e.target.matches('select, input[type="radio"]')) {
          var options = this._selectedOptions();
          this._applyVariant(this._findVariant(options), options);
          return;
        }
        if (e.target.matches('[data-sticky-variant]')) {
          var id = parseInt(e.target.value, 10);
          var variants = this.product.variants || [];
          for (var i = 0; i < variants.length; i++) {
            if (variants[i].id === id) {
              this._syncPickerTo(variants[i]);
              this._applyVariant(variants[i], variants[i].options.slice());
              return;
            }
          }
        }
      }

      _onClick(e) {
        var thumb = e.target.closest('[data-gallery-thumb]');
        if (thumb && this.contains(thumb)) {
          this._activateMedia(thumb.getAttribute('data-media-id'), true);
          return;
        }
        var dot = e.target.closest('[data-gallery-dot]');
        if (dot && this.contains(dot)) {
          this._activateMedia(dot.getAttribute('data-media-id'), true);
          return;
        }
        var zoom = e.target.closest('[data-lightbox-media]');
        if (zoom && this.contains(zoom)) {
          zoom.classList.remove('is-zooming');
          this._pendingLightbox = zoom.getAttribute('data-lightbox-media');
          return; // theme.js opens the modal via [data-modal-trigger]
        }
        var play = e.target.closest('[data-video-load]');
        if (play && this.contains(play)) {
          this._loadVideoFacade(play);
          return;
        }
        var share = e.target.closest('[data-share-button]');
        if (share && this.contains(share)) {
          this._share(share);
          return;
        }
        var stickyAdd = e.target.closest('[data-sticky-add]');
        if (stickyAdd && this.contains(stickyAdd)) {
          this._stickyAddToCart(stickyAdd);
          return;
        }
        var stickyBuy = e.target.closest('[data-sticky-buy]');
        if (stickyBuy && this.contains(stickyBuy)) {
          this._stickyBuyNow(stickyBuy);
        }
      }

      /* -- update pipeline -------------------------------------------------- */

      _applyVariant(variant, selectedOptions) {
        this.currentVariant = variant;
        this._updatePrice(variant);
        this._updateSaleState(variant);
        this._updateUnitPrice(variant);
        this._updateBuyButtons(variant);
        this._updateInventory(variant);
        this._updateStockBar(variant);
        this._updateSku(variant);
        this._updateMedia(variant);
        this._updateUrl(variant);
        this._updateStickyBar(variant);
        this._updatePickerState(selectedOptions || this._selectedOptions());
        this._updateFormStatus(variant);
        if (variant && this.form) {
          var hidden = this.form.querySelector('[data-main-variant-id]');
          if (hidden) {
            hidden.value = variant.id;
            hidden.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        // Reusable hook for CRO add-ons (volume pricing, gifts, upsells).
        this.dispatchEvent(new CustomEvent('variant:changed', {
          bubbles: true,
          detail: { variant: variant, sectionId: this.dataset.sectionId }
        }));
      }

      _updatePrice(variant) {
        if (!variant) return;
        var html;
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html = '<div class="price price--on-sale">' +
            '<span class="price__sale">' + formatMoney(variant.price) + '</span>' +
            '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>' +
            '</div>';
        } else {
          html = '<div class="price"><span class="price__regular">' + formatMoney(variant.price) + '</span></div>';
        }
        this.querySelectorAll('[data-price]').forEach(function (el) { el.innerHTML = html; });
      }

      /* Sale badge on the gallery + "Save N%" pill next to the price. */
      _updateSaleState(variant) {
        var onSale = !!(variant && variant.compare_at_price && variant.compare_at_price > variant.price);
        var pct = 0;
        if (onSale) {
          pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          if (pct <= 0) onSale = false;
        }
        this.querySelectorAll('[data-save-pill]').forEach(function (el) {
          if (onSale) {
            var pctEl = el.querySelector('[data-save-pct]');
            if (pctEl) pctEl.textContent = String(pct);
            else el.textContent = 'Save ' + pct + '%';
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        });
        var badge = this.querySelector('[data-gallery-badge]');
        if (badge) badge.hidden = !onSale;
      }

      /* Honest low-stock bar: real inventory only, hidden otherwise. */
      _updateStockBar(variant) {
        var el = this.querySelector('[data-stock-bar]');
        if (!el) return;
        var meta = variant && this.variantMeta[String(variant.id)];
        var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 0;
        var textEl = el.querySelector('[data-stock-bar-text]');
        var fill = el.querySelector('[data-stock-bar-fill]');
        var show = !!(variant && variant.available && meta && meta.managed &&
          meta.qty > 0 && threshold > 0 && meta.qty <= threshold);
        if (!show) {
          if (textEl) textEl.textContent = '';
          el.hidden = true;
          return;
        }
        var pct = Math.min(100, Math.round((meta.qty / threshold) * 100));
        if (fill) fill.style.width = pct + '%';
        if (textEl) {
          var template = el.getAttribute('data-text') || 'Only [count] left — order soon';
          textEl.textContent = template.replace('[count]', meta.qty);
        }
        el.hidden = false;
      }

      _updateUnitPrice(variant) {
        var els = this.querySelectorAll('[data-unit-price]');
        if (!els.length) return;
        var meta = variant && this.variantMeta[String(variant.id)];
        if (meta && meta.unitPrice != null) {
          var text = formatMoney(meta.unitPrice) + ' / ' + (meta.unitRef || '');
          els.forEach(function (el) { el.textContent = text; el.hidden = false; });
        } else {
          els.forEach(function (el) { el.hidden = true; });
        }
      }

      _buttonStrings(btn) {
        var strings = (window.theme && theme.strings) || {};
        return {
          add: btn.getAttribute('data-text-add') || 'Add to cart',
          soldout: btn.getAttribute('data-text-soldout') || strings.soldOut || 'Sold out',
          unavailable: btn.getAttribute('data-text-unavailable') || strings.unavailable || 'Unavailable'
        };
      }

      _setBuyButton(btn, textEl, variant) {
        if (!btn) return;
        var s = this._buttonStrings(btn);
        var label;
        if (!variant) {
          label = s.unavailable;
          btn.setAttribute('disabled', '');
        } else if (!variant.available) {
          label = s.soldout;
          btn.setAttribute('disabled', '');
        } else {
          label = s.add;
          btn.removeAttribute('disabled');
        }
        if (textEl) textEl.textContent = label;
        // Optional price shown inside the button (Add to cart · $29.00)
        var priceEl = btn.querySelector('[data-atc-price]');
        if (priceEl) {
          if (variant && variant.available) {
            priceEl.textContent = formatMoney(variant.price);
            priceEl.hidden = false;
          } else {
            priceEl.hidden = true;
          }
        }
      }

      _updateBuyButtons(variant) {
        if (this.form) {
          this._setBuyButton(
            this.form.querySelector('[data-add-to-cart]'),
            this.form.querySelector('[data-add-to-cart-text]'),
            variant
          );
        }
        if (this.stickyBar) {
          this._setBuyButton(
            this.stickyBar.querySelector('[data-sticky-add]'),
            this.stickyBar.querySelector('[data-sticky-add-text]'),
            variant
          );
        }
        var notify = this.querySelector('[data-notify-trigger]');
        if (notify) notify.hidden = !!(variant && variant.available);
      }

      _updateInventory(variant) {
        var el = this.querySelector('[data-inventory]');
        if (!el) return;
        var meta = variant && this.variantMeta[String(variant.id)];
        if (!variant || !meta || !meta.managed) {
          el.hidden = true;
          return;
        }
        var textEl = el.querySelector('[data-inventory-text]') || el;
        var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 0;
        el.classList.remove('product-info__inventory--low', 'product-info__inventory--out');
        var text;
        if (!variant.available) {
          text = el.getAttribute('data-text-out') || 'Out of stock';
          el.classList.add('product-info__inventory--out');
        } else if (meta.qty > 0 && meta.qty <= threshold) {
          text = (el.getAttribute('data-text-low') || 'Only [count] left').replace('[count]', meta.qty);
          el.classList.add('product-info__inventory--low');
        } else {
          text = el.getAttribute('data-text-in') || 'In stock';
        }
        textEl.textContent = text;
        el.hidden = false;
      }

      _updateSku(variant) {
        var wrap = this.querySelector('[data-sku-wrap]');
        if (!wrap) return;
        var sku = variant && variant.sku;
        var el = wrap.querySelector('[data-sku]');
        if (sku) {
          if (el) el.textContent = sku;
          wrap.hidden = false;
        } else {
          wrap.hidden = true;
        }
      }

      _updateMedia(variant) {
        if (!variant || !variant.featured_media) return;
        this._activateMedia(String(variant.featured_media.id), true);
      }

      /* Hover a colour swatch → preview that variant's media without selecting
         it; restore the selected variant's media on mouse-leave. Desktop
         pointers only, and never under reduced-motion. */
      _initVariantPreview() {
        if (!this.gallery || !this.picker || reducedMotion) return;
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        var self = this;
        this.picker.querySelectorAll('[data-preview-media]').forEach(function (el) {
          el.addEventListener('mouseenter', function () {
            var id = el.getAttribute('data-preview-media');
            if (id) self._activateMedia(id, false);
          });
          el.addEventListener('mouseleave', function () {
            if (self.currentVariant && self.currentVariant.featured_media) {
              self._activateMedia(String(self.currentVariant.featured_media.id), false);
            }
          });
        });
      }

      /* Lightweight size recommender — weight/height/fit → a size from the
         product's own Size option, which it can then select. No app, no data
         sent anywhere. */
      _initSizeRec() {
        var root = this.querySelector('[data-size-rec]');
        if (!root) return;
        var self = this;
        var toggle = root.querySelector('[data-size-rec-toggle]');
        var panel = root.querySelector('[data-size-rec-panel]');
        var result = root.querySelector('[data-sr-result]');
        var fit = 'regular';

        if (toggle && panel) {
          toggle.addEventListener('click', function () {
            var show = panel.hidden;
            panel.hidden = !show;
            toggle.setAttribute('aria-expanded', String(show));
          });
        }

        root.querySelectorAll('[data-sr-fit]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            fit = btn.getAttribute('data-sr-fit');
            root.querySelectorAll('[data-sr-fit]').forEach(function (b) {
              var on = b === btn;
              b.classList.toggle('is-active', on);
              b.setAttribute('aria-pressed', String(on));
            });
          });
        });

        var go = root.querySelector('[data-sr-go]');
        if (go) {
          go.addEventListener('click', function () {
            if (!result) return;
            var sizes = (root.getAttribute('data-sizes') || '').split('||').filter(Boolean);
            var unit = root.getAttribute('data-unit');
            var hEl = root.querySelector('[data-sr-height]');
            var wEl = root.querySelector('[data-sr-weight]');
            var h = parseFloat(hEl && hEl.value) || 0;
            var w = parseFloat(wEl && wEl.value) || 0;
            result.hidden = false;
            if (!w || !sizes.length) {
              result.textContent = 'Enter your weight to get a recommendation.';
              return;
            }
            var hcm = unit === 'imperial' ? h * 2.54 : h;
            var wkg = unit === 'imperial' ? w * 0.4536 : w;
            var bands = [52, 60, 68, 78, 90];
            var idx = 0;
            for (var i = 0; i < bands.length; i++) { if (wkg > bands[i]) idx = i + 1; }
            if (hcm >= 178) idx += 1;
            else if (hcm > 0 && hcm <= 158) idx -= 1;
            if (fit === 'slim') idx -= 1;
            else if (fit === 'relaxed') idx += 1;
            idx = Math.max(0, Math.min(idx, sizes.length - 1));
            var size = sizes[idx];

            result.textContent = '';
            var msg = document.createElement('span');
            msg.textContent = 'We recommend size ';
            var strong = document.createElement('strong');
            strong.textContent = size;
            msg.appendChild(strong);
            msg.appendChild(document.createTextNode('. '));
            var pick = document.createElement('button');
            pick.type = 'button';
            pick.className = 'size-rec__select';
            pick.setAttribute('data-sr-select', size);
            pick.textContent = 'Select';
            result.appendChild(msg);
            result.appendChild(pick);
          });
        }

        root.addEventListener('click', function (e) {
          var sel = e.target.closest('[data-sr-select]');
          if (!sel) return;
          self._selectSize(root.getAttribute('data-option-position'), sel.getAttribute('data-sr-select'));
        });
      }

      _selectSize(position, value) {
        if (!this.picker) return;
        var control = this.picker.querySelector('[data-option-position="' + position + '"]');
        if (!control) return;
        if (control.tagName === 'SELECT') {
          control.value = value;
          control.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        var inputs = control.querySelectorAll('input[type="radio"]');
        for (var i = 0; i < inputs.length; i++) {
          if (inputs[i].value === value) {
            inputs[i].checked = true;
            inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }

      /* Restock "notify me" — AJAX-submit the contact form so the drawer can
         show a success state without a page reload. */
      _initNotify() {
        var form = this.querySelector('[data-notify-form]');
        if (!form) return;
        var self = this;
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var body = self.querySelector('[data-notify-body]');
          var success = self.querySelector('[data-notify-success]');
          var errEl = form.querySelector('[data-notify-error]');
          var btn = form.querySelector('button[type="submit"]');
          if (errEl) errEl.hidden = true;
          if (btn) btn.classList.add('is-loading');
          fetch(form.action || window.location.pathname, { method: 'POST', body: new FormData(form), headers: { 'Accept': 'text/html' } })
            .then(function (r) {
              if (!r.ok) throw new Error('failed');
              if (body) body.hidden = true;
              if (success) success.hidden = false;
            })
            .catch(function () { if (errEl) errEl.hidden = false; })
            .finally(function () { if (btn) btn.classList.remove('is-loading'); });
        });
      }

      _updateUrl(variant) {
        if (!variant || !window.history || !history.replaceState) return;
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.pathname + url.search);
        } catch (e) { /* noop */ }
      }

      _updateStickyBar(variant) {
        if (!this.stickyBar || !variant) return;
        var price = this.stickyBar.querySelector('[data-sticky-price]');
        if (price) price.textContent = formatMoney(variant.price);
        var compare = this.stickyBar.querySelector('[data-sticky-compare]');
        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compare.textContent = formatMoney(variant.compare_at_price);
            compare.hidden = false;
          } else {
            compare.hidden = true;
          }
        }
        var select = this.stickyBar.querySelector('[data-sticky-variant]');
        if (select && select.value !== String(variant.id)) select.value = String(variant.id);
      }

      _updatePickerState(selected) {
        if (!this.picker || !selected.length) return;
        var self = this;
        var strings = (window.theme && theme.strings) || {};
        var soldOut = strings.soldOut || 'Sold out';

        this.picker.querySelectorAll('fieldset[data-option-position]').forEach(function (fieldset) {
          var pos = parseInt(fieldset.getAttribute('data-option-position'), 10);
          var label = fieldset.querySelector('[data-option-label]');
          if (label) label.textContent = selected[pos - 1] || '';
          fieldset.querySelectorAll('input[type="radio"]').forEach(function (input) {
            input.checked = input.value === selected[pos - 1];
            var pill = input.nextElementSibling;
            if (pill) {
              pill.classList.toggle('is-soldout', !self._optionHasAvailableVariant(pos, input.value, selected));
            }
          });
        });

        this.picker.querySelectorAll('select[data-option-position]').forEach(function (select) {
          var pos = parseInt(select.getAttribute('data-option-position'), 10);
          if (select.value !== selected[pos - 1]) select.value = selected[pos - 1];
          select.querySelectorAll('option').forEach(function (option) {
            var base = option.getAttribute('data-label') || option.value;
            var available = self._optionHasAvailableVariant(pos, option.value, selected);
            option.textContent = available ? base : base + ' — ' + soldOut;
          });
        });
      }

      _updateFormStatus(variant) {
        var status = this.form && this.form.querySelector('[data-form-status]');
        if (!status) return;
        var strings = (window.theme && theme.strings) || {};
        if (!variant) {
          status.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          status.textContent = strings.soldOut || 'Sold out';
        } else {
          status.textContent = '';
        }
      }

      _syncPickerTo(variant) {
        if (!this.picker || !variant) return;
        variant.options.forEach(function (value, i) {
          var pos = i + 1;
          this.picker.querySelectorAll('[data-option-position="' + pos + '"]').forEach(function (control) {
            if (control.tagName === 'SELECT') {
              control.value = value;
            } else {
              control.querySelectorAll('input[type="radio"]').forEach(function (input) {
                input.checked = input.value === value;
              });
            }
          });
        }, this);
      }

      /* -- media gallery ---------------------------------------------------- */

      _initGallery() {
        if (!this.gallery) return;
        var viewer = this.gallery.querySelector('[data-gallery-viewer]');
        /* The scroll listener is safe on the stacked layout too: on desktop the
           viewer is a non-scrolling grid (no scroll events), while under 750px
           it becomes the same horizontal snap strip as the thumbnails layout. */
        if (!viewer) return;
        var self = this;
        var sync = function () {
          var items = viewer.querySelectorAll('.product-gallery__item');
          if (!items.length) return;
          var viewerRect = viewer.getBoundingClientRect();
          var best = null;
          var bestDist = Infinity;
          items.forEach(function (item) {
            var d = Math.abs(item.getBoundingClientRect().left - viewerRect.left);
            if (d < bestDist) { bestDist = d; best = item; }
          });
          if (best) self._activateMedia(best.getAttribute('data-media-id'), false);
        };
        var debounced = window.theme && theme.utils && theme.utils.debounce ? theme.utils.debounce(sync, 100) : sync;
        viewer.addEventListener('scroll', debounced, { passive: true });
      }

      _activateMedia(mediaId, scroll) {
        if (!this.gallery || !mediaId) return;
        var item = this.gallery.querySelector('.product-gallery__item[data-media-id="' + mediaId + '"]');
        if (!item) return;
        var behavior = reducedMotion ? 'auto' : 'smooth';
        this.gallery.querySelectorAll('.product-gallery__item').forEach(function (el) {
          el.classList.toggle('is-active', el === item);
        });
        var activeThumb = null;
        this.gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
          if (thumb.getAttribute('data-media-id') === mediaId) {
            thumb.setAttribute('aria-current', 'true');
            activeThumb = thumb;
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        this.gallery.querySelectorAll('[data-gallery-dot]').forEach(function (dot) {
          if (dot.getAttribute('data-media-id') === mediaId) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
        /* Center the active thumb in its own strip (never scrolls the page). */
        if (activeThumb) {
          var thumbs = activeThumb.closest('.product-gallery__thumbs');
          if (thumbs) {
            thumbs.scrollTo({
              left: activeThumb.offsetLeft - (thumbs.clientWidth - activeThumb.offsetWidth) / 2,
              top: activeThumb.offsetTop - (thumbs.clientHeight - activeThumb.offsetHeight) / 2,
              behavior: behavior
            });
          }
        }
        if (!scroll) return;
        var viewer = this.gallery.querySelector('[data-gallery-viewer]');
        var horizontal = viewer && viewer.scrollWidth > viewer.clientWidth + 1;
        if (horizontal) {
          var left = viewer.scrollLeft + item.getBoundingClientRect().left - viewer.getBoundingClientRect().left;
          viewer.scrollTo({ left: left, behavior: behavior });
        } else if (this.gallery.classList.contains('product-gallery--stacked')) {
          item.scrollIntoView({ behavior: behavior, block: 'nearest' });
        }
      }

      /* Desktop hover zoom (magnifier): scale the image with the transform
         origin tracking the cursor. GPU-only, skipped for coarse pointers and
         reduced-motion users. Click-to-lightbox behavior is untouched. */
      _initZoom() {
        if (!this.gallery || reducedMotion) return;
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        var gallery = this.gallery;
        gallery.addEventListener('mousemove', function (e) {
          var zoom = e.target.closest('.product-gallery__zoom');
          if (!zoom || !gallery.contains(zoom)) return;
          var img = zoom.querySelector('.product-gallery__image');
          if (!img) return;
          var rect = zoom.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          var x = ((e.clientX - rect.left) / rect.width) * 100;
          var y = ((e.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = x.toFixed(2) + '% ' + y.toFixed(2) + '%';
          zoom.classList.add('is-zooming');
        });
        gallery.addEventListener('mouseout', function (e) {
          var zoom = e.target.closest('.product-gallery__zoom');
          if (!zoom || (e.relatedTarget && zoom.contains(e.relatedTarget))) return;
          zoom.classList.remove('is-zooming');
        });
      }

      _loadVideoFacade(button) {
        var facade = button.closest('[data-video-facade]');
        if (!facade) return;
        var template = facade.querySelector('template[data-video-template]');
        if (!template) return;
        var content = template.content.cloneNode(true);
        facade.textContent = '';
        facade.appendChild(content);
        facade.classList.add('is-loaded');
        var iframe = facade.querySelector('iframe');
        if (iframe) {
          iframe.removeAttribute('loading');
          iframe.focus();
        }
      }

      _initLightbox() {
        var modal = document.getElementById('ProductLightbox-' + this.sectionId);
        if (!modal) return;
        var self = this;
        modal.addEventListener('dialog:open', function () {
          var id = self._pendingLightbox;
          self._pendingLightbox = null;
          if (!id) {
            var active = self.gallery && self.gallery.querySelector('.product-gallery__item.is-active');
            if (active) id = active.getAttribute('data-media-id');
          }
          var target = id && modal.querySelector('[data-lightbox-item="' + id + '"]');
          // The dialog itself is the scroll container (.modal { overflow-y: auto })
          if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
        });
      }

      /* -- sticky add-to-cart bar -------------------------------------------- */

      _initStickyBar() {
        if (!this.stickyBar || !('IntersectionObserver' in window)) return;
        var target = this.form || this.querySelector('.product-layout__info');
        if (!target) return;
        var self = this;
        this._io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var top = entry.rootBounds ? entry.rootBounds.top : 0;
            var scrolledPast = entry.boundingClientRect.bottom < top;
            self._setStickyVisible(!entry.isIntersecting && scrolledPast);
          });
        }, { threshold: 0 });
        this._io.observe(target);
      }

      _setStickyVisible(show) {
        if (!this.stickyBar || show === this._stickyShown) return;
        this._stickyShown = show;
        var bar = this.stickyBar;
        if (show) {
          bar.hidden = false;
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { bar.classList.add('is-visible'); });
          });
        } else {
          bar.classList.remove('is-visible');
          var self = this;
          window.setTimeout(function () {
            if (!self._stickyShown) bar.hidden = true;
          }, reducedMotion ? 0 : 320);
        }
      }

      _stickyAddToCart(btn) {
        var variant = this.currentVariant;
        if (!variant || !variant.available) return;
        var qty = 1;
        var stickyQty = this.stickyBar && this.stickyBar.querySelector('[data-sticky-qty]');
        if (stickyQty) {
          qty = parseInt(stickyQty.value, 10) || 1;
        } else if (this.form) {
          var fd = new FormData(this.form);
          qty = parseInt(fd.get('quantity'), 10) || 1;
        }
        if (window.theme && theme.cart && typeof theme.cart.add === 'function') {
          btn.classList.add('is-loading');
          theme.cart.add({ id: variant.id, quantity: qty })
            .then(function () {
              if (window.theme.strings && theme.toast) theme.toast(theme.strings.addedToCart || 'Added to cart');
            })
            .catch(function (err) {
              if (window.theme && theme.toast) theme.toast(err.message || 'Could not add to cart');
            })
            .finally(function () { btn.classList.remove('is-loading'); });
        } else if (this.form) {
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
          else this.form.submit();
        }
      }

      /* Express "Buy now" from the sticky bar: add, then go straight to checkout. */
      _stickyBuyNow(btn) {
        var variant = this.currentVariant;
        if (!variant || !variant.available) return;
        var qty = 1;
        if (this.form) {
          var fd = new FormData(this.form);
          qty = parseInt(fd.get('quantity'), 10) || 1;
        }
        if (window.theme && theme.cart && typeof theme.cart.add === 'function') {
          btn.classList.add('is-loading');
          theme.cart.add({ id: variant.id, quantity: qty }, { openDrawer: false })
            .then(function () { window.location.href = '/checkout'; })
            .catch(function (err) {
              btn.classList.remove('is-loading');
              if (window.theme && theme.toast) theme.toast(err.message || 'Could not proceed to checkout');
            });
        }
      }

      /* -- share -------------------------------------------------------------- */

      _share(btn) {
        var url = btn.getAttribute('data-share-url') || window.location.href;
        var title = btn.getAttribute('data-share-title') || document.title;
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () { /* dismissed */ });
          return;
        }
        var copied = btn.getAttribute('data-text-copied') || 'Link copied';
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function () {
            if (window.theme && theme.toast) theme.toast(copied);
          }).catch(function () { /* noop */ });
        }
      }

      /* -- complementary products ---------------------------------------------- */

      _initComplementary() {
        var wrap = this.querySelector('[data-complementary]');
        if (!wrap) return;
        var url = wrap.getAttribute('data-url');
        if (!url) return;
        fetch(url)
          .then(function (r) {
            if (!r.ok) throw new Error('recommendations unavailable');
            return r.text();
          })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var results = null;
            doc.querySelectorAll('[data-complementary-results]').forEach(function (el) {
              if (!results && el.children.length) results = el;
            });
            if (!results) return;
            var target = wrap.querySelector('[data-complementary-results]');
            if (!target) return;
            target.innerHTML = results.innerHTML;
            wrap.hidden = false;
            wrap.classList.add('is-visible');
          })
          .catch(function () { /* keep hidden */ });
      }

      /* -- recently viewed writer ------------------------------------------------ */

      _writeRecentlyViewed() {
        try {
          var el = this.querySelector('[data-recently-viewed-json]');
          if (!el) return;
          var item = JSON.parse(el.textContent);
          if (!item || typeof item.handle !== 'string' || !item.handle.length) return;
          var list = [];
          try { list = JSON.parse(window.localStorage.getItem(RV_KEY) || '[]'); } catch (e) { list = []; }
          if (!Array.isArray(list)) list = [];
          list = list.filter(function (x) { return x && x.handle !== item.handle; });
          list.unshift({
            handle: item.handle,
            title: item.title,
            url: item.url,
            image: item.image,
            price: item.price
          });
          window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
        } catch (e) { /* storage unavailable */ }
      }
    });
  }
})();
